import { isApiError } from "@clubcore/api";
import { useErrorMessage } from "./feedback";

export interface FormErrors {
  fields: Record<string, string>;
  /** Message not tied to a field. */
  form?: string | undefined;
}

export interface FormErrorOptions {
  /** Field that receives a 409 (a duplicate name or number). */
  conflictField?: string;
  /** Field that receives a 404 (a chosen record that has since gone). */
  notFoundField?: string;
  /** Put specific error codes on a field, e.g. `{ wrong_password: "current_password" }`. */
  codeFields?: Record<string, string>;
}

/**
 * Turns an API failure into messages for a form. Validation errors (422) go to the field they name;
 * codes, 409 and 404 can be pointed at a field; everything else is a form-level message. Text comes
 * from the translated catalog by error code, and falls back to the server's own message.
 */
export function useFormErrors() {
  const describe = useErrorMessage();
  return (error: unknown, options: FormErrorOptions = {}): FormErrors => {
    if (!isApiError(error)) return { fields: {}, form: describe(error) };
    if (error.kind === "validation" && error.code === "validation_failed") {
      const fields = error.fieldErrors;
      return Object.keys(fields).length ? { fields, form: undefined } : { fields: {}, form: describe(error) };
    }
    const codeField = options.codeFields?.[error.code];
    if (codeField) return { fields: { [codeField]: describe(error) } };
    if (error.kind === "conflict" && options.conflictField) return { fields: { [options.conflictField]: describe(error) } };
    if (error.kind === "not_found" && options.notFoundField) return { fields: { [options.notFoundField]: describe(error) } };
    return { fields: {}, form: describe(error) };
  };
}

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
